import { configureStore } from "@reduxjs/toolkit"
import thunk from "redux-thunk"
import LogRocket from "logrocket"
import storage from "redux-persist/lib/storage"
import { persistReducer, createTransform } from "redux-persist"
import logger from "redux-logger"
import { isProd } from "utils-lib/builds"
import { isNextApp } from "utils-lib/isNextApp"
import makeRootReducer from "./reducers"

/**
 * Redux Persist transform that resets getLinkedAccountStatus on state rehydration.
 * @type {import('redux-persist').Transform}
 */
const userManageAccountTransform = createTransform(
  // transform state on its way to being serialized and persisted
  (inboundState) => inboundState,
  // transform state being rehydrated
  (outboundState) => ({
    ...outboundState,
    getLinkedAccountStatus: "", // Reset to empty string instead of keeping "pending"
  }),
  // configuration options
  { whitelist: ["userManageAccount"] },
)

/**
 * Creates and configures a Redux store with persistence, middleware, and dev tools.
 * @param {Object} [customReducers={}] - Additional reducers from host applications for incremental migration.
 * @param {string[]} [customWhitelist=[]] - Additional state slices to persist to localStorage.
 * @returns {import('@reduxjs/toolkit').EnhancedStore} Configured Redux store instance ready for use.
 */
const createStore = (customReducers = {}, customWhitelist = []) => {
  /**
   * Base middleware array starting with redux-thunk for async actions.
   * @type {import('redux').Middleware[]}
   */
  const middleware = [thunk]

  /**
   * Configuration object for redux-persist that defines which state slices should be persisted to localStorage.
   * @type {import('redux-persist').PersistConfig}
   */
  const persistConfig = {
    key: "redux",
    storage,
    // define the state you want to persist below
    whitelist: [
      // these are global shared reducers
      "alert",
      "cloudCraze",
      "communicationPreferences",
      "crossPollination",
      "currentNeighborhood",
      "customerContacts",
      "customerPrefVerification",
      "customerSelections",
      "customerSupportContact",
      "ecommerceCart",
      "flags",
      "holidaySchedule",
      "localsitesCdn",
      "myPayment",
      "myPaymentCheckout",
      "myPaymentConfirm",
      "myServices",
      "myServicesMainROStatus",
      "myWM",
      "notifications",
      "onlineProducts",
      "siteLanguage",
      "userAccount",
      "userBilling",
      "userManageAccount",
      "setIsRecycleQuizPage",
      "manageContacts",
      "topSupportTopicData",
      "authorTools",
      "processingFee",
      "guestContainerRepair",
      "containerRepairDamage",
      "contractRenewal",
      "serviceChange",
      "cancelService",
      "cancelServiceServelt",
      "rolloffAvailabilityData",
      "rolloffLeadCreationData",
      "fallbackServletServices",
      "userPersonalization",
      "personalization",
      "myPaymentGuest",
      "multiAccountDashboard",
      // these are coming from the host application (in the future, we can and should port over the rest of the reducers, this allows for an incremental approach without breaking anything)
      ...customWhitelist,
    ],
    transforms: [userManageAccountTransform], // Add the transform
  }

  /**
   * Root reducer enhanced with redux-persist capabilities.
   * @type {import('redux').Reducer}
   */
  const persistedReducer = persistReducer(
    persistConfig,
    makeRootReducer(customReducers),
  )

  // Conditionally add development middleware
  if (!isProd() && process.env.NODE_ENV !== `test`) {
    // Note: for next.js apps, only add logger middleware on the client side to prevent pollution of server logs
    // eslint-disable-next-line rulesdir/no-raw-window
    if (!isNextApp || (isNextApp && typeof window !== "undefined")) {
      middleware.push(logger)
    }
  }

  /**
   * Configured Redux Toolkit store with all middleware and dev tools enabled.
   * @type {import('@reduxjs/toolkit').EnhancedStore}
   */
  const store = configureStore({
    reducer: persistedReducer,
    devTools: process.env.NODE_ENV !== "production",
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        // Disabling these checks prevents excess mutation errors that the old version of redux let fail silently, going forward we can use the "immutable/no-mutation" eslint rule to find and fix mutations and then re-enable these checks.
        serializableCheck: false,
        immutableCheck: false,
      }).concat([...middleware, LogRocket.reduxMiddleware()]),
  })

  return store
}

/**
 * Default export of the createStore function.
 * @see {@link createStore} for detailed documentation
 */
export default createStore
